import { columnTypes } from '@/services/Grid/ColumnTypes';
import dayjs from 'dayjs';
import YesNoRenderer from '@/components/Grid/Renderers/YesNoRenderer';
import { cloneDeep } from 'lodash';
import store from '@/store';
import { isReduceRiskRatingEnabled } from '@/config/features';

export const newAndRenewalsDefaultColDef = {
  filter: 'agSetColumnFilter',
  resizable: true,
  sortable: true,
  filterParams: {
    selectAllOnMiniFilter: false,
  },
};

export const newAndRenewalsGridColumnTypes = columnTypes;

const newAndRenewalsGridColumns = [
  {
    headerName: 'ID',
    field: 'propertyId',
    minWidth: 120,
    width: 120,
    pinned: 'left',
    sort: 'desc',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
  }, {
    headerName: 'UPRN',
    field: 'uprn',
    minWidth: 140,
    width: 140,
  }, {
    headerName: 'Parent UPRN',
    field: 'parentUprn',
    minWidth: 140,
    width: 140,
  },  {
    headerName: 'Hierarchy Level',
    field: 'propertyHierarchyType',
    minWidth: 140,
    width: 140,
  },  {
    headerName: 'No Range',
    field: 'numberRange',
    minWidth: 120,
    width: 120,
  }, {
    headerName: 'Build Name',
    field: 'buildingName',
    minWidth: 180,
    width: 180,
  }, {
    headerName: 'Unit Number',
    field: 'unitNumber',
    minWidth: 120,
    width: 120,
  }, {
    headerName: 'Street No',
    field: 'streetNumber',
    minWidth: 120,
    width: 120,
  }, {
    headerName: 'Street',
    field: 'street',
    minWidth: 180,
    width: 180,
  }, {
    headerName: 'Postcode',
    field: 'postcode',
    minWidth: 120,
    width: 120,
  }, {
    headerName: 'Property Classification',
    field: 'riskProfileForPremise',
    minWidth: 210,
    width: 210,
  }, {
    headerName: 'Client Property Designation',
    field: 'clientPropertyDesignation',
    minWidth: 240,
    width: 240,
  }, {
    headerName: 'Riskhub Designation',
    field: 'propertyDesignation',
    minWidth: 200,
    width: 200,
  }, {
    headerName: 'Number of Storeys',
    field: 'numStoreys',
    minWidth: 170,
    width: 170,
  }, {
    headerName: 'Number of Flats',
    field: 'totalNumFlatBesiteBedroom',
    minWidth: 170,
    width: 170,
  }, {
    headerName: 'Tenure',
    field: 'tenure',
    minWidth: 125,
    width: 125,
  }, {
    headerName: 'Job Type',
    field: 'assessmentType',
    minWidth: 130,
    width: 130,
  }, {
    headerName: 'RA Expiry Date',
    field: 'dueDate',
    minWidth: 180,
    width: 130,
    type: 'dateColumn',
    cellClass: (params) => {
      const { programDueDate, dueDate } = params.data;

      const isDateSet = date => date !== '0000-00-00' && date !== null;
      // If due date and program due date are both present
      if (!isDateSet(programDueDate)
        || !isDateSet(dueDate)) {
        return '';
      }

      const dueDateObj = dayjs(dueDate);
      const programDueDateObj = dayjs(programDueDate);

      // AND they both have valid dates
      const validDates = dueDateObj.isValid()
        && programDueDateObj.isValid();

      // AND they differ
      // Then STRIKE through the cell
      if (validDates
        && !dueDateObj.isSame(programDueDateObj)) {
        return 'text-strike-through';
      }

      return '';
    },
  }, {
    headerName: 'Hand-in RA Risk Rating',
    field: 'assessmentRiskRating',
    minWidth: 200,
    width: 200,
  }, {
    headerName: 'Potential RA Risk Rating',
    field: 'assessmentPotentialRiskRating',
    colId: 'assessmentPotentialRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  }, {
    headerName: 'Current RA Risk Rating',
    field: 'assessmentCurrentRiskRating',
    colId: 'assessmentCurrentRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  }, {
    headerName: 'RA Job Status',
    field: 'assessmentStatus',
    minWidth: 180,
    width: 180,
  }, {
    headerName: 'Query Type',
    field: 'queryType',
    minWidth: 130,
    width: 130,
  }, {
    headerName: 'RA Availability',
    field: 'complianceStatus',
    minWidth: 180,
    width: 180,
  }, {
    headerName: 'Job ID',
    field: 'jobId',
    minWidth: 100,
    width: 100,
  }, {
    headerName: 'Job Request ID',
    field: 'jobRequestId',
    minWidth: 170,
    width: 170,
  }, {
    headerName: 'Program Due Date',
    field: 'programDueDate',
    minWidth: 130,
    width: 130,
    type: 'dateColumn',
  }, {
    headerName: 'Cat B',
    field: 'catB',
    minWidth: 130,
    width: 130,
    cellRendererFramework: YesNoRenderer,
  }, {
    headerName: 'Handover date',
    field: 'handoverDate',
    minWidth: 130,
    width: 130,
    type: 'dateColumn',
  }, {
    headerName: 'Building Height',
    field: 'buildingHeight',
    minWidth: 130,
    width: 130,
  },
  {
    headerName: 'Building Height Source',
    field: 'buildingHeightSource',
    minWidth: 130,
    width: 130,
  }, {
    headerName: 'Current Evac Strategy',
    field: 'currentEvacStrategy',
    minWidth: 170,
    width: 170,
  }, {
    headerName: 'Recommended Evac Strategy',
    field: 'recommendedEvacStrategy',
    minWidth: 170,
    width: 170,
  },
];

export const getNewAndRenewalsGridColumns = () => {
  const cols = cloneDeep(newAndRenewalsGridColumns);
  const user = store.getters && store.getters.authUser;
  // If feature flag disabled, drop the column; otherwise keep static definition/position
  if(!isReduceRiskRatingEnabled(user)) {
    return cols.filter(c => c && !['assessmentPotentialRiskRating', 'assessmentCurrentRiskRating'].includes(c.field));
  }
  return cols;
};
